import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat } from "@/utils";
import { fmtMoney } from "@/utils/money";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import "./TradeInvoicePage.scss";
import type { Invoice } from "@zhihaoscm/entity";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Table as ZhTable,
    Upload as ZhUpload,
    type UploadRequestOptions,
    type UploadUserFile,
    type TableColumnProps
} from "zhihao-ui-react";

export interface TradeInvoicePageResult {
    list: Invoice[];
    total: number;
}

export interface TradeInvoiceSummary {
    totalAmount: number;
    totalCount: number;
}

export type TradeInvoicePreviewFile = (fileId: number | string) => Promise<string | undefined>;
export type TradeInvoiceInvalidator = (
    id: number | string,
    params: { reasons: string; fileId?: string }
) => Promise<unknown>;

export interface TradeInvoiceListQuery {
    page: number;
    size: number;
    invoiceId?: string;
    orderId?: string;
    purchaserInfo?: string;
    state?: string;
    beginTime?: string;
    endTime?: string;
}

export interface TradeInvoicePageProps {
    loadInvoices?: (query: TradeInvoiceListQuery) => Promise<TradeInvoicePageResult>;
    loadSummary?: () => Promise<TradeInvoiceSummary>;
    previewInvoiceFile?: TradeInvoicePreviewFile;
    invalidateInvoice?: TradeInvoiceInvalidator;
    permissions?: string[];
}

/** 将 Vue 发票分页返回的状态枚举统一为列表文案使用的数字。 */
function normalizeTradeInvoiceRows(rows?: Invoice[]): Invoice[] {
    return (rows || []).map(row => ({
        ...row,
        state: Number(row.state)
    }));
}

/** 按旧 Vue formatter 组合采购用户实名和手机号，缺失字段使用可用信息回退。 */
function purchaserLabel(row: Invoice): string {
    const purchaser = row.purchaserEnterprise;
    if (purchaser?.realName && purchaser.mobile) return `${purchaser.realName}(${purchaser.mobile})`;
    return purchaser?.realName || purchaser?.mobile || "--";
}

/** 复用 Vue 发票管理分页接口，保留订单号筛选参数。 */
export async function loadTradeInvoices(query: {
    page: number;
    size: number;
    invoiceId?: string;
    orderId?: string;
    purchaserInfo?: string;
    state?: string;
    beginTime?: string;
    endTime?: string;
}): Promise<TradeInvoicePageResult> {
    const response = await request.service.get<TradeInvoicePageResult>("/admin/invoice/paging", { params: query });
    return response.data;
}

/** 复用 Vue 发票统计接口，保留已开票总金额和笔数两个卡片字段。 */
export async function loadTradeInvoiceSummary(): Promise<TradeInvoiceSummary> {
    const response = await request.service.get<TradeInvoiceSummary>("/admin/invoice/calc-total-count");
    return response.data;
}

/** 查询 Vue 文件服务并返回预览地址，保持发票列表的历史文件接口。 */
export async function previewTradeInvoiceFile(fileId: number | string): Promise<string | undefined> {
    const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${fileId}`);
    return response.data.path;
}

/** 复用 Vue 发票作废接口，原因通过 query 参数提交。 */
export async function invalidateTradeInvoiceFromList(
    id: number | string,
    params: { reasons: string; fileId?: string }
): Promise<unknown> {
    const response = await request.service.put(`/admin/invoice/invalid/${id}`, undefined, { params });
    return response.data;
}

/** 将日期输入转换为 Vue 发票接口约定的当天起止时间。 */
function formatInvoiceDateRange(date: string, suffix: "start" | "end"): string {
    return `${date}T${suffix === "start" ? "00:00:00" : "23:59:59"}`;
}

/** 交易订单关联发票页，完整复用 Vue 发票列表的订单筛选、字段和操作。 */
export function TradeInvoicePage({
    loadInvoices = loadTradeInvoices,
    loadSummary = loadTradeInvoiceSummary,
    previewInvoiceFile = previewTradeInvoiceFile,
    invalidateInvoice = invalidateTradeInvoiceFromList,
    permissions
}: TradeInvoicePageProps) {
    const location = useLocation();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 对齐 Vue v-auth：发票新增/作废需要 DEAL，预览需要 R；支持测试和嵌入场景显式传入权限。
    const currentPermissions = permissions ?? storePermissions;
    const canRead = currentPermissions.includes("ROLE_INVOICE_R");
    const canDeal = currentPermissions.includes("ROLE_INVOICE_DEAL");
    const orderId = new URLSearchParams(location.search).get("orderId") || undefined;
    const [draftFilters, setDraftFilters] = useState({
        invoiceId: "",
        orderId: orderId || "",
        purchaserInfo: "",
        state: "",
        createdStart: "",
        createdEnd: ""
    });
    const [query, setQuery] = useState<TradeInvoiceListQuery>({ page: 1, size: 10, ...(orderId ? { orderId } : {}) });
    const [items, setItems] = useState<Invoice[]>([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState<TradeInvoiceSummary>({ totalAmount: 0, totalCount: 0 });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [invalidatingInvoiceId, setInvalidatingInvoiceId] = useState<number | string | null>(null);
    const [invalidatingFileId, setInvalidatingFileId] = useState<string | undefined>(undefined);
    const [invalidateFileList, setInvalidateFileList] = useState<UploadUserFile[]>([]);
    const [invalidateReason, setInvalidateReason] = useState("");
    const [invalidateError, setInvalidateError] = useState<string | null>(null);

    /** 清理作废弹窗状态，保证再次打开时复用统一表单初始态。 */
    const closeInvalidationDialog = () => {
        setInvalidatingInvoiceId(null);
        setInvalidatingFileId(undefined);
        setInvalidateFileList([]);
        setInvalidateReason("");
        setInvalidateError(null);
    };

    /** 对齐 Vue 作废文件字段，限制上传文件不超过 10MB。 */
    const validateInvalidationFile = (file: File) => {
        if (file.size > 10 * 1024 * 1024) {
            setInvalidateError("作废文件最大不超过10M");
            return false;
        }
        setInvalidateError(null);
        return true;
    };

    /** 复用全局文件服务上传作废凭证并回填接口需要的 fileId。 */
    const uploadInvalidationFile = async ({ file, onError, onSuccess }: UploadRequestOptions) => {
        const formData = new FormData();
        formData.append("file", file);
        try {
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            if (!uploadedFile.id) throw new Error("上传响应缺少文件 ID");
            setInvalidatingFileId(String(uploadedFile.id));
            setInvalidateFileList([
                { name: uploadedFile.name || file.name, response: uploadedFile, url: uploadedFile.path }
            ]);
            setInvalidateError(null);
            onSuccess(uploadedFile);
        } catch {
            setInvalidateError("发票作废文件上传失败");
            onError(new Error("发票作废文件上传失败"));
        }
    };

    const updateDraft = (key: keyof typeof draftFilters, value: string) => {
        setDraftFilters(current => ({ ...current, [key]: value }));
    };

    /** 清空发票筛选项；从订单进入时保留路由携带的订单编号。 */
    const resetFilters = () => {
        setDraftFilters({
            invoiceId: "",
            orderId: orderId || "",
            purchaserInfo: "",
            state: "",
            createdStart: "",
            createdEnd: ""
        });
        setErrorMessage(null);
        setQuery({ page: 1, size: 10, ...(orderId ? { orderId } : {}) });
    };

    const submitFilters = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const nextQuery: TradeInvoiceListQuery = { page: 1, size: 10 };
        const createdStart = draftFilters.createdStart.trim();
        const createdEnd = draftFilters.createdEnd.trim();

        // 日期范围必须成对提交，且开始日期不能晚于结束日期，避免生成无效接口条件。
        if (Boolean(createdStart) !== Boolean(createdEnd)) {
            setErrorMessage("请选择完整创建日期范围");
            return;
        }
        if (createdStart && createdEnd && createdStart > createdEnd) {
            setErrorMessage("创建开始日期不能晚于结束日期");
            return;
        }
        if (createdStart && createdEnd) {
            nextQuery.beginTime = formatInvoiceDateRange(createdStart, "start");
            nextQuery.endTime = formatInvoiceDateRange(createdEnd, "end");
        }

        for (const [key, value] of Object.entries(draftFilters)) {
            if (key === "createdStart" || key === "createdEnd") continue;
            if (value.trim()) nextQuery[key as keyof Omit<TradeInvoiceListQuery, "page" | "size">] = value.trim();
        }
        setQuery(nextQuery);
    };

    const previewFile = async (row: Invoice) => {
        const fileId = row.invalidFileId || row.fileId;
        if (!fileId) return;
        try {
            const path = await previewInvoiceFile(fileId);
            if (path) window.open(path, "_blank");
        } catch {
            setErrorMessage("发票文件预览失败");
        }
    };

    const submitInvalidation = async () => {
        const reason = invalidateReason.trim();
        if (!reason || invalidatingInvoiceId == null) {
            setInvalidateError("请输入作废原因");
            return;
        }
        try {
            // 沿用 Vue 作废接口的 query 参数，将原发票文件 ID 一并传给后端。
            await invalidateInvoice(invalidatingInvoiceId, {
                reasons: reason,
                ...(invalidatingFileId ? { fileId: String(invalidatingFileId) } : {})
            });
            setInvalidatingInvoiceId(null);
            setInvalidatingFileId(undefined);
            setInvalidateFileList([]);
            setInvalidateReason("");
            setErrorMessage(null);
            setInvalidateError(null);
            // 用新对象触发当前查询重新加载，保留用户正在查看的筛选条件和页码。
            setQuery(current => ({ ...current }));
        } catch {
            setErrorMessage("发票作废失败");
        }
    };

    useEffect(() => {
        let disposed = false;
        void loadInvoices(query)
            .then(result => {
                if (!disposed) {
                    setItems(normalizeTradeInvoiceRows(result.list));
                    setTotal(result.total || 0);
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setItems([]);
                    setTotal(0);
                    setErrorMessage("发票列表加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadInvoices, query]);

    useEffect(() => {
        let disposed = false;
        void loadSummary()
            .then(result => {
                if (!disposed)
                    setSummary({
                        totalAmount: Number(result.totalAmount) || 0,
                        totalCount: Number(result.totalCount) || 0
                    });
            })
            .catch(() => {
                if (!disposed) setErrorMessage("发票统计加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadSummary]);

    /** 将 Vue 发票列表字段映射为 zhihao Table 列，保留原有文件和作废操作。 */
    const invoiceColumns: TableColumnProps<Invoice>[] = [
        {
            label: "开票编号",
            width: 160,
            prop: "id",
            render: ({ row }) => (row.id ? <Link to={`/tradeManage/invoiceDetail?id=${row.id}`}>{row.id}</Link> : "-")
        },
        {
            label: "订单编号",
            width: 160,
            prop: "orderId",
            render: ({ row }) =>
                row.orderId ? <Link to={`/tradeManage/tradeOrder/detail?id=${row.orderId}`}>{row.orderId}</Link> : "-"
        },
        {
            label: "采购用户",
            minWidth: 280,
            prop: "purchaserEnterprise",
            render: ({ row }) => purchaserLabel(row)
        },
        {
            label: "开票金额(元)",
            minWidth: 130,
            prop: "amount",
            render: ({ row }) => fmtMoney(row.amount)
        },
        {
            label: "创建时间",
            minWidth: 188,
            prop: "createdTime",
            render: ({ row }) => (row.createdTime ? dateFormat(row.createdTime, DATETIME_PICKER_FORMAT) : "--")
        },
        {
            label: "开票日期",
            minWidth: 188,
            prop: "invoiceDate",
            render: ({ row }) => dateFormat(row.invoiceDate)
        },
        {
            label: "状态",
            width: 78,
            prop: "state",
            render: ({ row }) => (row.state === 1 ? "已开票" : "已作废")
        },
        {
            label: "操作",
            fixed: "right",
            width: 120,
            prop: "operation",
            className: "admin-table-operation-column compact",
            labelClassName: "admin-table-operation-column compact",
            render: ({ row }) => (
                <>
                    {canRead ? (
                        <Button type="link" onClick={() => void previewFile(row)}>
                            预览
                        </Button>
                    ) : null}
                    {row.state === 1 && canDeal ? (
                        <Button
                            type="link"
                            onClick={() => {
                                setInvalidatingInvoiceId(row.id);
                                setInvalidatingFileId(row.fileId ? String(row.fileId) : undefined);
                            }}
                        >
                            作废
                        </Button>
                    ) : null}
                </>
            )
        }
    ];

    return (
        <section aria-label="trade-invoice-page" className="admin-trade-list-page flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable
                    className="flex-1"
                    columns={normalizeOperationColumns(invoiceColumns, items)}
                    data={items}
                    pagination={false}
                    rowKey={row => String(row.id)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                发票管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <div className="admin-trade-stat-cards" aria-label="发票统计">
                                <Card className="admin-trade-stat-card" size="small">
                                    <Typography.Text className="admin-trade-stat-card__label">
                                        已开票总金额(元)
                                    </Typography.Text>
                                    <Typography.Title className="admin-trade-stat-card__value" level={3}>
                                        {summary.totalAmount.toLocaleString("en-US", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </Typography.Title>
                                </Card>
                                <Card className="admin-trade-stat-card" size="small">
                                    <Typography.Text className="admin-trade-stat-card__label">
                                        已开票(笔)
                                    </Typography.Text>
                                    <Typography.Title
                                        className="admin-trade-stat-card__value admin-trade-stat-card__value--primary"
                                        level={3}
                                    >
                                        {summary.totalCount.toLocaleString("en-US")}
                                    </Typography.Title>
                                </Card>
                            </div>

                            <form className="admin-table-filter-form mb-4" onSubmit={event => submitFilters(event)}>
                                {canDeal ? (
                                    <Link
                                        aria-label="新增发票"
                                        className="admin-table-primary-action"
                                        to="/tradeManage/invoiceAdd"
                                    >
                                        <Button type="primary">新增</Button>
                                    </Link>
                                ) : null}
                                <label>
                                    发票编号
                                    <Input
                                        aria-label="发票编号"
                                        placeholder="请输入开票编号搜索"
                                        value={draftFilters.invoiceId}
                                        onChange={event => updateDraft("invoiceId", event.target.value)}
                                    />
                                </label>
                                <label>
                                    订单编号
                                    <Input
                                        aria-label="订单编号"
                                        placeholder="请输入订单编号搜索"
                                        value={draftFilters.orderId}
                                        onChange={event => updateDraft("orderId", event.target.value)}
                                    />
                                </label>
                                <label>
                                    采购账号
                                    <Input
                                        aria-label="采购账号"
                                        placeholder="请输入采购用户账号信息搜索"
                                        value={draftFilters.purchaserInfo}
                                        onChange={event => updateDraft("purchaserInfo", event.target.value)}
                                    />
                                </label>
                                <label>
                                    创建日期
                                    <ZhDateRangePicker
                                        type="daterange"
                                        format="YYYY-MM-DD"
                                        valueFormat="YYYY-MM-DD"
                                        rangeSeparator="-"
                                        startPlaceholder="创建开始日期"
                                        endPlaceholder="创建结束日期"
                                        value={[draftFilters.createdStart || null, draftFilters.createdEnd || null]}
                                        onChange={(_value, formattedValue) => {
                                            updateDraft("createdStart", formattedValue?.[0] || "");
                                            updateDraft("createdEnd", formattedValue?.[1] || "");
                                        }}
                                    />
                                </label>
                                <label>
                                    发票状态
                                    <Select
                                        aria-label="发票状态"
                                        allowClear
                                        className="min-w-32"
                                        options={[
                                            { label: "已开票", value: "1" },
                                            { label: "已作废", value: "2" }
                                        ]}
                                        placeholder="请选择状态"
                                        // Ant Design Select 只有在 value 为 undefined 时才会渲染 placeholder；空字符串会造成截图中的空白下拉框。
                                        value={draftFilters.state || undefined}
                                        onChange={value => updateDraft("state", value || "")}
                                    />
                                </label>
                                <Button
                                    aria-label="查询发票"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置发票"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    onClick={resetFilters}
                                />
                            </form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4 self-end"
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                total={total}
                                onChange={(page, size) =>
                                    setQuery(current => ({
                                        ...current,
                                        // 旧版 ProTable 切换每页条数后回到第一页，普通翻页保留当前页。
                                        page: size !== current.size ? 1 : page,
                                        size
                                    }))
                                }
                            />
                        </div>
                    }
                />
                <ZhDialog
                    className="trade-invoice-invalidate-dialog"
                    onCancel={closeInvalidationDialog}
                    open={invalidatingInvoiceId != null}
                    title="作废发票"
                    width={400}
                    footer={
                        <Space>
                            <Button onClick={closeInvalidationDialog}>取消</Button>
                            <Button onClick={() => void submitInvalidation()} type="primary">
                                提交
                            </Button>
                        </Space>
                    }
                >
                    {invalidateError ? (
                        <Alert className="mb-4" message={invalidateError} role="alert" type="error" />
                    ) : null}
                    <Form className="trade-invoice-invalidate-form" labelAlign="right" labelCol={{ span: 5 }}>
                        <Form.Item label="作废原因" required>
                            <Input.TextArea
                                aria-label="作废原因"
                                maxLength={200}
                                placeholder="请输入作废原因"
                                rows={4}
                                showCount
                                value={invalidateReason}
                                onChange={event => {
                                    setInvalidateReason(event.target.value);
                                    if (event.target.value.trim()) setInvalidateError(null);
                                }}
                            />
                        </Form.Item>
                        <Form.Item className="mb-0" label="作废文件">
                            {invalidateFileList.length ? (
                                <Space>
                                    <span>{invalidateFileList[0]?.name}</span>
                                    <Button
                                        htmlType="button"
                                        size="small"
                                        onClick={() => {
                                            setInvalidatingFileId(undefined);
                                            setInvalidateFileList([]);
                                        }}
                                    >
                                        删除
                                    </Button>
                                </Space>
                            ) : (
                                <ZhUpload
                                    action="#"
                                    accept="image/*,.pdf,.doc,.docx"
                                    aria-label="作废文件"
                                    limit={1}
                                    name="作废文件"
                                    beforeUpload={validateInvalidationFile}
                                    httpRequest={uploadInvalidationFile}
                                >
                                    <Button htmlType="button" type="link">
                                        <span
                                            aria-hidden="true"
                                            className="iconfont icon-a-Frame30"
                                            style={{ marginRight: 5 }}
                                        />
                                        上传文件
                                    </Button>
                                </ZhUpload>
                            )}
                            {!invalidateFileList.length ? (
                                <Typography.Text className="ml-4" type="secondary">
                                    最大不超过10M
                                </Typography.Text>
                            ) : null}
                        </Form.Item>
                    </Form>
                </ZhDialog>
            </Card>
        </section>
    );
}

export default TradeInvoicePage;
